import React from "react";
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from "../utils/chunkReload";

interface Props {
  children: React.ReactNode;
}

interface State {
  // true mientras manejamos un error capturado
  errored: boolean;
  // true si el error fue de carga de chunk (vs. cualquier otro error de render)
  isChunk: boolean;
}

/**
 * Boundary alrededor de las rutas lazy (`/estadisticas`, `/estadisticas-admin`).
 *
 * Cuando un import dinámico falla porque el chunk quedó obsoleto tras un deploy
 * (el navegador pide un hash viejo → el server devuelve el index.html de
 * fallback → `ChunkLoadError`), recarga la página UNA vez para bajar el bundle
 * nuevo. El guard vive en `reloadOnceForChunkError()` (sessionStorage) para no
 * entrar en bucle.
 *
 * Para errores que NO son de chunk, muestra un fallback mínimo en vez de dejar
 * la pantalla en blanco (sin este boundary, un error de render desmonta el
 * árbol completo). El fallback es texto plano: no depende de cargar más chunks.
 */
class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { errored: false, isChunk: false };

  static getDerivedStateFromError(error: unknown): State {
    return { errored: true, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) {
      const reloaded = reloadOnceForChunkError();
      // Si el guard frenó la recarga (ya recargamos hace poco y volvió a
      // fallar), no es caché vieja sino un build roto: bajamos a fallback
      // genérico ("recarga la página") en vez de quedar en "Actualizando…".
      if (!reloaded) this.setState({ isChunk: false });
    }
  }

  render(): React.ReactNode {
    if (this.state.errored) {
      return (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--text-secondary)",
          }}
        >
          {this.state.isChunk
            ? "Actualizando la aplicación…"
            : "Algo salió mal al cargar esta sección. Recarga la página."}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
